import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Row, Col, Card, CardBody, Button, Input, Spinner, Alert, Badge,
} from "reactstrap";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import { API } from "../../api/urls";
import AttachmentPicker from "./AttachmentPicker";
import MessageAttachments from "./MessageAttachments";
import TicketTimeline from "./TicketTimeline";
import { useTicketAttachments } from "./useTicketAttachments";
import { buildMessagePayload } from "./buildMessagePayload";
import { fetchSupportBadge, fetchMySupportBadge } from "../../store/supportBadge/actions";
import { useAuth } from "../../hooks/useAuth";
import {
    StatusPill, PriorityPill, CATEGORY_META, STATUS_OPTIONS, PRIORITY_OPTIONS,
    STATUS_META, fmtDateTime,
} from "./ticketMeta";
import BackButton from "../../components/Common/BackButton";

const NAVY = "#221C5D";
// Mesaj listesi bu yüksekliğe kadar büyür, sonrasında kendi içinde scroll olur.
// Viewport'a göreli: yanıt kutusu her ekran boyutunda görünür kalır.
const MESSAGES_MAX_HEIGHT = "55vh";
// Backend spam koruması bu durum koduyla yanıt verir (RateLimiterService).
const TOO_MANY_REQUESTS = 429;

export default function TicketDetailPage({ admin = false }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // Mesaj balonlarının hizası bu kimliğe göre belirlenir (kendi mesajın sağda).
    const { userId } = useAuth();

    // Talebin "yanıt bekliyor" durumu değiştiğinde ilgili menü rozetini tazele:
    // personel görünümünde destek kuyruğu, kullanıcı görünümünde Taleplerim rozeti.
    const refreshSupportBadge = useCallback(() => {
        dispatch(admin ? fetchSupportBadge() : fetchMySupportBadge());
    }, [admin, dispatch]);

    const urls = admin
        ? { get: API.admin_ticket, reply: API.admin_ticket_reply, update: API.admin_ticket_update }
        : { get: API.ticket, reply: API.ticket_reply, close: API.ticket_close };

    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const [savingMeta, setSavingMeta] = useState(false);
    const [assignees, setAssignees] = useState([]);

    const messagesRef = useRef(null);
    const messageCount = ticket?.messages?.length ?? 0;
    const prevMessageCountRef = useRef(0);

    // Talep açıldığında son mesaj görünür olsun; yeni mesajda listeyi akıcı şekilde en alta indir.
    // Kullanıcı yukarı scroll edip eski mesajları okuyabilir — sadece mesaj sayısı değişince tetiklenir.
    useEffect(() => {
        const el = messagesRef.current;
        if (!el || messageCount === 0) return;
        const isInitialLoad = prevMessageCountRef.current === 0;
        el.scrollTo({ top: el.scrollHeight, behavior: isInitialLoad ? "auto" : "smooth" });
        prevMessageCountRef.current = messageCount;
    }, [messageCount]);

    const load = useCallback(async () => {
        try {
            setLoading(true); setError(null);
            const { data } = await axios.get(urls.get(id));
            if (data.statusCode !== 200) { setError(data.message || "Talep yüklenemedi"); return; }
            setTicket(data.data);
        } catch (err) {
            if (err.response?.status === 401) return navigate("/login");
            setError(err.response?.data?.message || "Talep yüklenemedi");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Atanabilir personel listesi (yalnızca admin görünümünde)
    useEffect(() => {
        if (!admin) return;
        axios.get(API.admin_ticket_assignees)
            .then(({ data }) => setAssignees(data.data || []))
            .catch(() => setAssignees([]));
    }, [admin]);

    const assignUser = async (assigneeId) => {
        setSavingMeta(true);
        try {
            const { data } = await axios.patch(API.admin_ticket_assign(id), { assigneeId: assigneeId || null });
            setTicket(data.data);
            toast.success(assigneeId ? "Talep atandı" : "Atama kaldırıldı");
        } catch (err) {
            toast.error(err.response?.data?.message || "Atama güncellenemedi");
        } finally {
            setSavingMeta(false);
        }
    };

    const attachments = useTicketAttachments(toast.error);

    const sendReply = async () => {
        if (!reply.trim()) return;
        setSending(true);
        try {
            const { data } = await axios.post(
                urls.reply(id),
                buildMessagePayload({ message: reply }, attachments.files, attachments.pastedImages));
            setTicket(data.data);
            setReply("");
            attachments.clear();
            refreshSupportBadge();
        } catch (err) {
            // 429: spam koruması. Yazılan metin korunur, istek otomatik tekrarlanmaz;
            // kullanıcı süre dolduktan sonra kendisi tekrar gönderir.
            if (err.response?.status === TOO_MANY_REQUESTS) {
                toast.warn(err.response?.data?.message || "Çok sık mesaj gönderiyorsunuz. Lütfen biraz bekleyin.");
            } else {
                toast.error(err.response?.data?.message || "Yanıt gönderilemedi");
            }
        } finally {
            setSending(false);
        }
    };

    const updateMeta = async (patch) => {
        setSavingMeta(true);
        try {
            const { data } = await axios.patch(urls.update(id), patch);
            setTicket(data.data);
            refreshSupportBadge();
            toast.success("Talep güncellendi");
        } catch (err) {
            toast.error(err.response?.data?.message || "Güncellenemedi");
        } finally {
            setSavingMeta(false);
        }
    };

    const closeTicket = async () => {
        setSavingMeta(true);
        try {
            const { data } = await axios.patch(urls.close(id));
            setTicket(data.data);
            toast.success("Talep kapatıldı");
        } catch (err) {
            toast.error(err.response?.data?.message || "Kapatılamadı");
        } finally {
            setSavingMeta(false);
        }
    };

    const backTo = admin ? "/admin/tickets" : "/support";

    if (loading) return <div className="page-content"><div className="text-center p-5"><Spinner color="primary" /></div></div>;
    if (error || !ticket) return (
        <div className="page-content"><div className="container-fluid">
            <BackButton to={backTo} className="mb-3" />
            <Alert color="danger">{error || "Talep bulunamadı"}</Alert>
        </div></div>
    );

    const isClosed = ticket.status === "CLOSED";

    return (
        <div className="page-content">
            <div className="container-fluid">
                <BackButton to={backTo} className="mb-3" />

                <Row>
                    <Col lg={8}>
                        <Card className="rounded-4 border-0 shadow-sm mb-3">
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                                    <h5 className="fw-bold text-dark mb-0">{ticket.subject}</h5>
                                    <div className="d-flex gap-2"><StatusPill status={ticket.status} /><PriorityPill priority={ticket.priority} /></div>
                                </div>
                                <div className="text-muted" style={{ fontSize: ".82rem" }}>
                                    <span className="me-3"><i className="bx bx-category me-1" />{CATEGORY_META[ticket.category] || ticket.category}</span>
                                    {/* Talebi açan kullanıcı. Destek görünümünde kullanıcı detayına
                                        gidilebilir; müşteri görünümünde /users erişimi olmadığı için düz metin. */}
                                    <span className="me-3">
                                        <i className="bx bx-user me-1" />
                                        {admin && ticket.createdByUserId ? (
                                            <Link to={`/users/${ticket.createdByUserId}`} title="Kullanıcı detayına git">
                                                {ticket.createdByUsername}
                                            </Link>
                                        ) : (
                                            ticket.createdByUsername
                                        )}
                                    </span>
                                    {/* Talep bir cariden geldiyse önce cari, sonra bağlı olduğu firma yazılır. */}
                                    {ticket.createdByAccountName && (
                                        <span className="me-3"><i className="bx bx-briefcase me-1" />{ticket.createdByAccountName}</span>
                                    )}
                                    {ticket.companyName && (
                                        <span className="me-3"><i className="bx bx-buildings me-1" />{ticket.companyName}</span>
                                    )}
                                    <span><i className="bx bx-time me-1" />{fmtDateTime(ticket.createdAt)}</span>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Mesaj thread'i */}
                        <Card className="rounded-4 border-0 shadow-sm mb-3">
                            <CardBody>
                                <div
                                    ref={messagesRef}
                                    style={{
                                        maxHeight: MESSAGES_MAX_HEIGHT,
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        overscrollBehavior: "contain",
                                    }}
                                >
                                {(ticket.messages || []).map((m) => {
                                    // WhatsApp mantığı: yazan taraf her zaman sağda. Karar metni fiilen
                                    // yazana bakar — personelin müşteri adına yazdığı mesajda sahibi
                                    // müşteri, yazan personeldir. writtenByUserId yoksa yazan = sahibi.
                                    const writer = m.writtenByUserId || m.senderUserId;
                                    const isOwnMessage = !!userId && writer === userId;
                                    // Sahibi ile yazan farklıysa balon bunu açıkça söylemeli.
                                    const relayedByStaff = !!m.writtenByUserId && m.writtenByUserId !== m.senderUserId;
                                    return (
                                        <div key={m.id} className="d-flex mb-3" style={{ justifyContent: isOwnMessage ? "flex-end" : "flex-start" }}>
                                            <div style={{
                                                maxWidth: "80%",
                                                background: isOwnMessage ? "#eaf0f8" : "#f8fafc",
                                                border: "1px solid #e9edf5",
                                                borderRadius: 14,
                                                padding: ".7rem .9rem",
                                            }}>
                                                <div className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: ".75rem" }}>
                                                    {/* Balonda mesajı yazan kişi görünür; adına yazılan
                                                        mesajlarda bu, talep sahibi değil personeldir. */}
                                                    <span className="fw-semibold" style={{ color: m.staff ? NAVY : "#334155" }}>
                                                        {m.writtenByUsername || m.senderUsername}
                                                    </span>
                                                    {m.staff && <Badge color="primary" pill style={{ fontSize: ".6rem" }}>Destek</Badge>}
                                                    {relayedByStaff && (
                                                        <Badge color="secondary" pill style={{ fontSize: ".6rem" }}>
                                                            destek kaydetti
                                                        </Badge>
                                                    )}
                                                    <span className="text-muted">· {fmtDateTime(m.createdAt)}</span>
                                                </div>
                                                <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: ".9rem", color: "#1f2a44" }}>{m.body}</div>
                                                <MessageAttachments ticketId={id} attachments={m.attachments} />
                                            </div>
                                        </div>
                                    );
                                })}
                                </div>

                                {/* Yanıt kutusu */}
                                {isClosed ? (
                                    <Alert color="secondary" className="mb-0 mt-2">Bu talep kapatıldı.</Alert>
                                ) : (
                                    <div className="mt-2">
                                        <Input type="textarea" rows={3} value={reply} placeholder="Yanıtınızı yazın..."
                                            onChange={(e) => setReply(e.target.value)}
                                            onPaste={attachments.handlePaste} />
                                        <div className="mt-2">
                                            <AttachmentPicker
                                                items={attachments.items}
                                                onAdd={attachments.add}
                                                onRemove={attachments.remove}
                                                disabled={sending}
                                            />
                                        </div>
                                        <div className="d-flex justify-content-end mt-2">
                                            <Button style={{ background: NAVY, borderColor: NAVY }} onClick={sendReply} disabled={sending || !reply.trim()}>
                                                {sending ? <Spinner size="sm" /> : <><i className="bx bx-send" /> Gönder</>}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>

                        {/* Hareket geçmişi — mesajlaşmanın altında, detay yanıtındaki timeline'dan beslenir. */}
                        <TicketTimeline entries={ticket.timeline} admin={admin} />
                    </Col>

                    {/* Yan panel */}
                    <Col lg={4}>
                        <Card className="rounded-4 border-0 shadow-sm">
                            <CardBody>
                                <h6 className="fw-bold text-dark mb-3">Talep Bilgileri</h6>
                                <div className="mb-2" style={{ fontSize: ".85rem" }}>
                                    <div className="text-muted">Atanan Personel</div>
                                    {admin ? (
                                        <Input type="select" bsSize="sm" value={ticket.assignedToUserId || ""}
                                            disabled={savingMeta}
                                            onChange={(e) => assignUser(e.target.value)}>
                                            <option value="">— Atanmamış —</option>
                                            {assignees.map((a) => (
                                                <option key={a.id} value={a.id}>{a.username}</option>
                                            ))}
                                        </Input>
                                    ) : (
                                        <div>{ticket.assignedToUsername || "—"}</div>
                                    )}
                                </div>
                                {/* Talep kimin adına, hangi firma için açıldı — personelin adına
                                    açtığı taleplerde bunu talebe girmeden de görebilmek gerekiyor. */}
                                <div className="mb-2" style={{ fontSize: ".85rem" }}>
                                    <div className="text-muted">Firma</div>
                                    <div>{ticket.companyName || "—"}</div>
                                </div>
                                <div className="mb-2" style={{ fontSize: ".85rem" }}>
                                    <div className="text-muted">Talep Sahibi</div>
                                    <div>{ticket.createdByUsername || "—"}</div>
                                </div>
                                <div className="mb-3" style={{ fontSize: ".85rem" }}>
                                    <div className="text-muted">Son Yanıt</div>
                                    <div>{fmtDateTime(ticket.lastReplyAt)}</div>
                                </div>

                                {admin ? (
                                    <>
                                        <div className="mb-2">
                                            <label className="text-muted small">Durum</label>
                                            <Input type="select" bsSize="sm" value={ticket.status} disabled={savingMeta}
                                                onChange={(e) => updateMeta({ status: e.target.value })}>
                                                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                                            </Input>
                                        </div>
                                        <div className="mb-2">
                                            <label className="text-muted small">Öncelik</label>
                                            <Input type="select" bsSize="sm" value={ticket.priority} disabled={savingMeta}
                                                onChange={(e) => updateMeta({ priority: e.target.value })}>
                                                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p === "LOW" ? "Düşük" : p === "MEDIUM" ? "Orta" : p === "HIGH" ? "Yüksek" : "Acil"}</option>)}
                                            </Input>
                                        </div>
                                    </>
                                ) : (
                                    !isClosed && (
                                        <Button outline color="secondary" size="sm" className="w-100" onClick={closeTicket} disabled={savingMeta}>
                                            <i className="bx bx-check-circle" /> Talebi Kapat
                                        </Button>
                                    )
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* App.js'teki container "global-toaster" id'si ile tanımlı ve isimsiz toast'ları almaz;
                    diğer sayfalar (UsersTable, SettingsPage) gibi sayfa bazlı container kullanılıyor. */}
                <ToastContainer position="top-right" autoClose={3000} />
            </div>
        </div>
    );
}
